export type ApiValidationIssue = {
  type?: string;
  msg: string;
  loc?: (string | number)[];
  input?: unknown;
  /** Расширение для POST …/move/validate: id слотов в зале, участвующих в конфликте. */
  related_slot_ids?: number[];
};

function sanitizeMessage(message: string, status?: number): string {
  const text = message.trim();
  if (!text) {
    return status != null && status >= 500
      ? 'Сервис временно недоступен. Попробуйте позже.'
      : 'Неизвестная ошибка';
  }
  const htmlish = /<\s*html[\s>]|<\s*body[\s>]|<\s*head[\s>]|<\s*title[\s>]|<\s*h1[\s>]/i.test(text);
  if (!htmlish) return text;
  if (status != null && status >= 500) return 'Сервис временно недоступен. Попробуйте позже.';
  return 'Сервер вернул некорректный ответ.';
}

function formatValidationLoc(loc: (string | number)[] | undefined): string {
  if (!loc?.length) return '';
  const tail = loc.filter((x) => x !== 'body' && x !== 'query' && x !== 'path' && x !== 'header');
  if (!tail.length) return '';
  return tail.map(String).join('.');
}

export function formatApiErrorDetail(detail: unknown): { message: string; items?: ApiValidationIssue[] } {
  if (detail == null) return { message: 'Неизвестная ошибка' };
  if (typeof detail === 'string') return { message: sanitizeMessage(detail) };
  if (Array.isArray(detail)) {
    const items: ApiValidationIssue[] = [];
    const lines: string[] = [];
    let i = 0;
    for (const entry of detail) {
      if (entry && typeof entry === 'object' && 'msg' in entry) {
        const e = entry as Record<string, unknown>;
        const msg = String(e.msg ?? '');
        const locRaw = Array.isArray(e.loc) ? (e.loc as (string | number)[]) : undefined;
        const type = typeof e.type === 'string' ? e.type : undefined;
        let relatedSlotIds: number[] | undefined;
        const relRaw = e.related_slot_ids;
        if (Array.isArray(relRaw)) {
          const nums = relRaw
            .map((x) => (typeof x === 'number' ? x : Number(x)))
            .filter((n) => Number.isFinite(n));
          if (nums.length) relatedSlotIds = nums;
        }
        items.push({ type, msg, loc: locRaw, input: e.input, related_slot_ids: relatedSlotIds });
        const locLabel = formatValidationLoc(locRaw);
        i += 1;
        lines.push(locLabel ? `${i}. ${locLabel}: ${msg}` : `${i}. ${msg}`);
      } else if (typeof entry === 'string') {
        i += 1;
        lines.push(`${i}. ${entry}`);
      }
    }
    return {
      message: lines.length ? lines.join('\n') : 'Ошибка валидации',
      items: items.length ? items : undefined
    };
  }
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    const o = detail as Record<string, unknown>;
    if (typeof o.message === 'string') return { message: sanitizeMessage(o.message) };
    if ('msg' in o) return { message: sanitizeMessage(String(o.msg)) };
  }
  return { message: sanitizeMessage(String(detail)) };
}

export type ParsedApiErrorBody = {
  detailMessage: string;
  detailItems?: ApiValidationIssue[];
  errorType?: string;
  errorCode?: string;
  /** `details` из error envelope (например, `field` для 422 «Налогов и чеков»). */
  details?: Record<string, unknown>;
  field?: string;
  permission?: string;
  tempToken?: string;
  moduleCode?: string;
  /** type=module_dependency: недостающие module_code с сервера. */
  requires?: string[];
};

export function parseApiErrorJsonBody(
  status: number,
  payload: unknown
): ParsedApiErrorBody | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const p = payload as Record<string, unknown>;
  if (!('detail' in p)) return null;
  const { message, items } = formatApiErrorDetail(p.detail);
  const requiresRaw = p.requires;
  const requires =
    Array.isArray(requiresRaw) && requiresRaw.every((x) => typeof x === 'string')
      ? (requiresRaw as string[])
      : undefined;
  const details =
    p.details && typeof p.details === 'object' && !Array.isArray(p.details)
      ? (p.details as Record<string, unknown>)
      : undefined;
  const field =
    typeof p.field === 'string'
      ? p.field
      : details && typeof details.field === 'string'
        ? details.field
        : undefined;
  return {
    detailMessage: sanitizeMessage(message, status),
    detailItems: items,
    errorType: typeof p.type === 'string' ? p.type : undefined,
    errorCode: typeof p.code === 'string' ? p.code : undefined,
    details,
    field,
    permission: typeof p.permission === 'string' ? p.permission : undefined,
    tempToken: typeof p.temp_token === 'string' ? p.temp_token : undefined,
    moduleCode: typeof p.module === 'string' ? p.module : typeof p.module_code === 'string' ? p.module_code : undefined,
    requires
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly detailItems?: ApiValidationIssue[];
  readonly errorType?: string;
  readonly errorCode?: string;
  readonly details?: Record<string, unknown>;
  readonly field?: string;
  readonly permission?: string;
  readonly tempToken?: string;
  readonly moduleCode?: string;
  readonly requires?: string[];
  readonly rawBody?: unknown;

  constructor(
    status: number,
    detail: string,
    opts?: {
      detailItems?: ApiValidationIssue[];
      errorType?: string;
      errorCode?: string;
      details?: Record<string, unknown>;
      field?: string;
      permission?: string;
      tempToken?: string;
      moduleCode?: string;
      requires?: string[];
      rawBody?: unknown;
    }
  ) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.detailItems = opts?.detailItems;
    this.errorType = opts?.errorType;
    this.errorCode = opts?.errorCode;
    this.details = opts?.details;
    this.field = opts?.field;
    this.permission = opts?.permission;
    this.tempToken = opts?.tempToken;
    this.moduleCode = opts?.moduleCode;
    this.requires = opts?.requires;
    this.rawBody = opts?.rawBody;
  }

  static fromResponse(status: number, rawText: string, contentType: string): ApiError {
    const isJson = contentType.includes('application/json');
    if (!rawText.trim()) {
      return new ApiError(status, status === 0 ? 'Нет соединения с сервером' : `HTTP ${status}`);
    }
    if (!isJson) {
      return new ApiError(status, sanitizeMessage(rawText, status));
    }
    try {
      const payload = JSON.parse(rawText) as unknown;
      const parsed = parseApiErrorJsonBody(status, payload);
      if (parsed) {
        return new ApiError(status, parsed.detailMessage, {
          detailItems: parsed.detailItems,
          errorType: parsed.errorType,
          errorCode: parsed.errorCode,
          details: parsed.details,
          field: parsed.field,
          permission: parsed.permission,
          tempToken: parsed.tempToken,
          moduleCode: parsed.moduleCode,
          requires: parsed.requires,
          rawBody: payload
        });
      }
    } catch {
      /* fallthrough */
    }
    return new ApiError(status, sanitizeMessage(rawText, status));
  }
}
